import type { LanguageCode } from '../../types';
import type { ConsentPreferences } from '../privacy/ConsentService';

type AnalyticsScalar = string | number | boolean | null;

export interface KimAnalyticsEvent {
  name: string;
  path: string;
  title?: string;
  language?: LanguageCode;
  metadata?: Record<string, AnalyticsScalar>;
}

export class KimAnalyticsService {
  private readonly endpoint = '/api/analytics/events';
  private readonly visitStorageKey = 'kim-converter-analytics-visit';
  private analyticsAllowed = false;
  private lastPageViewKey: string | null = null;

  public sync(preferences: ConsentPreferences | null): void {
    this.analyticsAllowed = Boolean(preferences?.analytics);

    if (!this.analyticsAllowed) {
      this.lastPageViewKey = null;
      return;
    }

    this.trackVisit();
  }

  public trackPageView(event: Pick<KimAnalyticsEvent, 'path' | 'title' | 'language'> & { route: string }): void {
    if (!this.analyticsAllowed) {
      return;
    }

    const pageViewKey = `${event.path}|${event.title ?? ''}`;
    if (pageViewKey === this.lastPageViewKey) {
      return;
    }

    this.lastPageViewKey = pageViewKey;
    this.trackEvent('page_view', {
      path: event.path,
      title: event.title,
      language: event.language,
      metadata: { route: event.route },
    });
  }

  public trackEvent(name: string, event?: Partial<KimAnalyticsEvent>): void {
    if (!this.analyticsAllowed) {
      return;
    }

    const payload = {
      events: [
        {
          name: this.normalizeName(name),
          path: this.normalizePath(event?.path ?? window.location.pathname),
          title: truncate(event?.title ?? document.title, 160),
          language: event?.language,
          metadata: sanitizeMetadata(event?.metadata),
        },
      ],
    };

    this.send(payload);
  }

  private trackVisit(): void {
    try {
      if (sessionStorage.getItem(this.visitStorageKey) === '1') {
        return;
      }

      sessionStorage.setItem(this.visitStorageKey, '1');
    } catch {
      // Session storage can be unavailable in strict privacy modes; counting a visit is optional.
    }

    this.trackEvent('site_visit', {
      metadata: {
        viewport: getViewportBucket(),
      },
    });
  }

  private send(payload: { events: KimAnalyticsEvent[] }): void {
    const body = JSON.stringify(payload);

    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(this.endpoint, blob)) {
        return;
      }
    }

    void fetch(this.endpoint, {
      body,
      headers: {
        'Content-Type': 'application/json',
      },
      keepalive: true,
      method: 'POST',
    }).catch(() => undefined);
  }

  private normalizeName(value: string): string {
    return truncate(value.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_'), 64) || 'event';
  }

  private normalizePath(value: string): string {
    return truncate(value.split('?')[0]?.split('#')[0] || '/', 180);
  }
}

function sanitizeMetadata(metadata: KimAnalyticsEvent['metadata'] | undefined): Record<string, AnalyticsScalar> {
  if (!metadata) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(metadata)
      .slice(0, 12)
      .map(([key, value]) => [truncate(key.replace(/[^a-zA-Z0-9_-]+/g, '_'), 48), sanitizeMetadataValue(value)]),
  );
}

function sanitizeMetadataValue(value: unknown): AnalyticsScalar {
  if (typeof value === 'string') {
    return truncate(value, 120);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'boolean' || value === null) {
    return value;
  }

  return null;
}

function getViewportBucket(): string {
  const width = window.innerWidth;
  if (width < 720) {
    return 'mobile';
  }

  if (width < 1040) {
    return 'tablet';
  }

  return 'desktop';
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}
